import { get, post } from '@src/services/NetworkUtils'

// Wraps the path-scoped /v1/companies/:companyId/billing/* endpoints exposed
// by backoffice-service. The onprem service may proxy to saw-service (when
// BILLING_MODE=cloud) — the FE doesn't care which.

export const BillingService = {
  // Quota balance + invoice/transaction listings
  getBalance: (companyId) => get(`/v1/companies/${companyId}/billing/balance`),
  getInvoices: (companyId, params) =>
    get(`/v1/companies/${companyId}/billing/invoices`, params),
  getInvoiceDetail: (companyId, invoiceId) =>
    get(`/v1/companies/${companyId}/billing/invoices/${invoiceId}`),
  cancelInvoice: (companyId, invoiceId) =>
    post(`/v1/companies/${companyId}/billing/invoices/${invoiceId}/cancel`),
  getTransactions: (companyId, params) =>
    get(`/v1/companies/${companyId}/billing/transactions`, params),

  // Quota usage — ledger of movements (top-up / charge + running balance) and
  // the quota currently on hold for in-flight enquiries.
  getQuotaLedger: (companyId, params) =>
    get(`/v1/companies/${companyId}/billing/usage/ledger`, params),
  getQuotaHolds: (companyId) =>
    get(`/v1/companies/${companyId}/billing/usage/holds`),

  // Payment methods the payer may actually choose — the ones an operator routed
  // to a live gateway, in display order. Pass { amount_idr } and each entry
  // carries its own app_fee_idr, priced by the same helper that bills it.
  // Send `wire_code` back as payment_methods[0]; `code` is internal vocabulary.
  getPaymentMethods: (companyId, params) =>
    get(`/v1/companies/${companyId}/billing/payment-methods`, params),

  // Pricing plans — pass { plan_type: 'subscription' } untuk filter sub plans
  getPricingPlans: (companyId, params) =>
    get(`/v1/companies/${companyId}/billing/pricing-plans`, params),

  // Legacy one-time invoice creation (still used in legacy flows kalau ada)
  createInvoice: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/invoices`, data),

  // ─── Subscription ──────────────────────────────────────────────────────────
  getSubscription: (companyId) =>
    get(`/v1/companies/${companyId}/billing/subscription`),
  subscribe: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/subscribe`, data),
  // Quote a subscribe without buying it. The FE must NOT recompute this: when
  // saldo covers part of the bill, PPN applies only to the REMAINDER paid
  // through the gateway (BR-PPN-03), and the same server helper prices both
  // this preview and the invoice that follows.
  previewSubscribe: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/subscribe/preview`, data),
  cancelSubscription: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/subscription/cancel`, data),
  resumeSubscription: (companyId) =>
    post(`/v1/companies/${companyId}/billing/subscription/resume`),
  changePlan: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/subscription/change-plan`, data),
  // Price a package switch before committing. Same server helper prices this
  // and the switch itself, so the dialog and the ledger cannot disagree.
  previewChange: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/subscription/change-preview`, data),
  cancelScheduledChange: (companyId) =>
    post(`/v1/companies/${companyId}/billing/subscription/cancel-scheduled-change`),
  generateSubscriptionInvoice: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/subscription/invoice`, data),

  // ─── Topup quota (priced via active subscription's per_enquiry_fee) ────────
  topup: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/topup`, data),

  // ─── Top up saldo Rupiah (BR-SALDO-04) ────────────────────────────────────
  // `amount_idr` is the BASE — what lands in the wallet. PPN and the gateway
  // fee are added on top, so the customer pays `breakdown.total_idr`. Never
  // send the total here: it would be credited as saldo (BR-PPN-02).
  createSaldoTopup: (companyId, data) =>
    post(`/v1/companies/${companyId}/billing/saldo-topup`, data),
}
